import Button from "components/ui/Button";
import Input from "components/ui/Input";
import { Bot, Maximize2, Minimize2, Send, User, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useChatMutation } from "store/api/aiChatApi";

interface SalesProperty {
	id: string;
	title: string;
	address: string;
	type: string;
	price: number;
	status: string;
	bedrooms: number;
	bathrooms: number;
	square_feet: number;
	images: string[];
	description: string;
	created_at: string;
	viewings: number;
	inquiries: number;
}

interface Message {
	id: number;
	text: string;
	sender: "user" | "bot";
	timestamp: Date;
}

interface LiveChatInterfaceProps {
	property?: SalesProperty | null;
	onClose: () => void;
}

const LiveChatInterface: React.FC<LiveChatInterfaceProps> = ({
	property,
	onClose,
}) => {
	const [chat, { isLoading: isChatLoading }] = useChatMutation();
	const [messages, setMessages] = useState<Message[]>([
		{
			id: 1,
			text: property
				? `Hello! I'm here to help you with inquiries about ${property?.title}. What would you like to know?`
				: "Hello! I'm here to help you with any property inquiries. How can I assist you today?",
			sender: "bot",
			timestamp: new Date(),
		},
	]);
	const [inputMessage, setInputMessage] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isMinimized, setIsMinimized] = useState(false);
	const [conversationId, setConversationId] = useState<string | null>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const scrollToBottom = () => {
		messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" });
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	const handleSendMessage = async (e: React.FormEvent<HTMLFormElement>) => {
		e?.preventDefault();
		if (!inputMessage?.trim() || isLoading || isChatLoading) return;

		const userMessage: Message = {
			id: Date.now(),
			text: inputMessage,
			sender: "user",
			timestamp: new Date(),
		};

		setMessages((prev) => [...prev, userMessage]);
		const messageToSend = inputMessage;
		setInputMessage("");
		setIsLoading(true);

		try {
			// Use backend API with conversation_id for context
			// Backend handles property context automatically
			const response = await chat({
				message: messageToSend,
				property_id: property?.id || undefined,
				conversation_id: conversationId || undefined,
			}).unwrap();

			// Update conversation ID if this is a new conversation
			if (response.conversation_id && !conversationId) {
				setConversationId(response.conversation_id);
			}

			const botMessage: Message = {
				id: Date.now() + 1,
				text:
					response.message ||
					"I apologize, but I couldn't generate a response. Please try again.",
				sender: "bot",
				timestamp: new Date(),
			};

			setMessages((prev) => [...prev, botMessage]);
		} catch (error: any) {
			console.error("Chat error:", error);

			// Remove the user message on error
			setMessages((prev) => prev.slice(0, -1));

			// Show user-friendly error message
			const errorMessage: Message = {
				id: Date.now() + 1,
				text:
					error?.data?.error ||
					error?.data?.message ||
					"I apologize, but I'm having trouble connecting right now. Please try again in a moment, or feel free to contact us directly for immediate assistance.",
				sender: "bot",
				timestamp: new Date(),
			};
			setMessages((prev) => [...prev, errorMessage]);
		} finally {
			setIsLoading(false);
		}
	};

	const formatTime = (timestamp: Date) => {
		return timestamp?.toLocaleTimeString("en-US", {
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const quickResponses = [
		"What's the price range?",
		"Can I schedule a viewing?",
		"Tell me about the neighborhood",
		"What are the property features?",
		"Is financing available?",
	];

	const handleQuickResponse = (text: string) => {
		setInputMessage(text);
		inputRef?.current?.focus();
	};

	return (
		<div
			className={`fixed right-4 bottom-4 z-50 bg-card border border-border rounded-lg shadow-2xl transition-all duration-300 ${
				isMinimized ? "w-80 h-14" : "w-96 h-[600px]"
			}`}
		>
			{/* Header */}
			<div className="flex items-center justify-between p-4 border-b border-border bg-primary text-primary-foreground rounded-t-lg">
				<div className="flex items-center gap-3">
					<div className="w-8 h-8 bg-primary-foreground/20 rounded-full flex items-center justify-center">
						<Bot className="w-4 h-4" />
					</div>
					<div>
						<h3 className="font-semibold text-sm">
							{property
								? `Chat about ${property?.title}`
								: "Real Estate Assistant"}
						</h3>
						<div className="flex items-center gap-1 text-xs opacity-90">
							<div className="w-2 h-2 bg-green-400 rounded-full"></div>
							<span>Online</span>
						</div>
					</div>
				</div>

				<div className="flex items-center gap-1">
					<Button
						variant="ghost"
						size="icon"
						onClick={() => setIsMinimized(!isMinimized)}
						className="text-primary-foreground hover:bg-primary-foreground/20"
					>
						{isMinimized ? (
							<Maximize2 className="w-4 h-4" />
						) : (
							<Minimize2 className="w-4 h-4" />
						)}
					</Button>
					<Button
						variant="ghost"
						size="icon"
						onClick={onClose}
						className="text-primary-foreground hover:bg-primary-foreground/20"
					>
						<X className="w-4 h-4" />
					</Button>
				</div>
			</div>
			{!isMinimized && (
				<>
					{/* Property Info */}
					{property && (
						<div className="p-3 bg-muted border-b border-border">
							<div className="flex items-center gap-3">
								<div className="w-12 h-12 bg-background rounded-lg overflow-hidden">
									{property?.images?.[0] ? (
										<img
											src={property?.images?.[0]}
											alt={property?.title}
											className="w-full h-full object-cover"
										/>
									) : (
										<div className="w-full h-full bg-muted flex items-center justify-center">
											<Bot className="w-4 h-4 text-muted-foreground" />
										</div>
									)}
								</div>
								<div className="flex-1 min-w-0">
									<h4 className="font-medium text-sm text-foreground truncate">
										{property?.title}
									</h4>
									<p className="text-xs text-muted-foreground truncate">
										{property?.address}
									</p>
									<p className="text-sm font-semibold text-foreground">
										₦
										{(Number(property?.price) || 0).toLocaleString("en-NG", {
											maximumFractionDigits: 0,
										})}
									</p>
								</div>
							</div>
						</div>
					)}

					{/* Messages */}
					<div className="flex-1 overflow-y-auto p-4 space-y-4 h-[400px]">
						{messages?.map((message) => (
							<div
								key={message?.id}
								className={`flex ${
									message?.sender === "user" ? "justify-end" : "justify-start"
								}`}
							>
								<div
									className={`flex items-start gap-2 max-w-[80%] ${
										message?.sender === "user" ? "flex-row-reverse" : "flex-row"
									}`}
								>
									<div
										className={`w-6 h-6 rounded-full flex items-center justify-center ${
											message?.sender === "user"
												? "bg-primary text-primary-foreground"
												: "bg-muted text-muted-foreground"
										}`}
									>
										{message?.sender === "user" ? (
											<User className="w-3 h-3" />
										) : (
											<Bot className="w-3 h-3" />
										)}
									</div>

									<div
										className={`rounded-lg p-3 ${
											message?.sender === "user"
												? "bg-primary text-primary-foreground"
												: "bg-muted text-foreground"
										}`}
									>
										<p className="text-sm">{message?.text}</p>
										<p className={`text-xs mt-1 opacity-70`}>
											{formatTime(message?.timestamp)}
										</p>
									</div>
								</div>
							</div>
						))}

						{isLoading && (
							<div className="flex justify-start">
								<div className="flex items-start gap-2">
									<div className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
										<Bot className="w-3 h-3" />
									</div>
									<div className="bg-muted rounded-lg p-3">
										<div className="flex items-center gap-1">
											<div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
											<div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce delay-100"></div>
											<div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce delay-200"></div>
										</div>
									</div>
								</div>
							</div>
						)}

						<div ref={messagesEndRef} />
					</div>

					{/* Quick Responses */}
					<div className="p-3 border-t border-border">
						<div className="flex flex-wrap gap-2 mb-3">
							{quickResponses?.map((response, index) => (
								<button
									key={index}
									onClick={() => handleQuickResponse(response)}
									className="text-xs bg-muted hover:bg-muted/80 text-foreground px-2 py-1 rounded-full transition-colors"
								>
									{response}
								</button>
							))}
						</div>
					</div>

					{/* Message Input */}
					<form
						onSubmit={handleSendMessage}
						className="p-4 border-t border-border"
					>
						<div className="flex items-center gap-2">
							<Input
								ref={inputRef}
								value={inputMessage}
								onChange={(e) => setInputMessage(e?.target?.value)}
								placeholder="Type your message..."
								disabled={isLoading}
								className="flex-1 text-sm"
							/>
							<Button
								type="submit"
								size="icon"
								disabled={!inputMessage?.trim() || isLoading}
								className="shrink-0"
							>
								<Send className="w-4 h-4" />
							</Button>
						</div>
					</form>
				</>
			)}
		</div>
	);
};

export default LiveChatInterface;
